import axios from 'axios';
import {
  convertHeicIfNeeded,
  getFileExtension,
} from '../../../components/image_uploads/utils';
import { CF_DOMAIN } from '../../../constants/aws';
import { loader } from 'graphql.macro';
import { MAX_FILE_UPLOAD_SIZE, toastVariant } from '../../../constants/misc';
import { showToast } from '../../../graphql/cache/modal';
import { v4 as uuidv4 } from 'uuid';

const uploadUrlQuery = loader('../../../graphql/queries/s3.graphql');

export const uploadImageFile = async ({
  fileUUID,
  file,
  description,
  onSubmit = () => {},
  signatureText = null,
  nonImageFileName = null,
  previousAdditional = null,
  company = null,
  client = null,
}) => {
  if (!company?.id) {
    showToast({
      variant: toastVariant.danger,
      title: 'Error',
      message:
        'Upload failed: company information not available. Please refresh and try again.',
    });
    return;
  }

  const fileName = fileUUID;
  const fileType = getFileExtension(file);

  try {
    const { data } = await client.query({
      query: uploadUrlQuery,
      variables: {
        fileName: `assets/${company.id}/${fileName}.${fileType}`,
        fileType,
      },
    });

    const signedUrl = data.simpleStorageUploadUrl;
    const options = {
      headers: {
        'Content-Type': fileType === 'pdf' ? 'application/pdf' : fileType,
      },
    };
    const result = await axios.put(signedUrl, file, options);

    if (result.status === 200) {
      const imageUrl = `${CF_DOMAIN({ company })}${fileName}.${fileType}`;
      if (signatureText) {
        const newAdditional = previousAdditional?.length
          ? previousAdditional.concat('|' + signatureText)
          : signatureText;
        onSubmit(imageUrl, false, 'PASS', newAdditional);
      } else if (nonImageFileName) {
        const newAdditional = previousAdditional
          ? `${previousAdditional}|${nonImageFileName}`
          : `${nonImageFileName}`;
        onSubmit({
          url: imageUrl,
          name: newAdditional,
        });
      } else {
        onSubmit(imageUrl, description);
      }
      return imageUrl;
    }
  } catch (error) {
    throw new Error(`Upload failed: ${error}`);
  }
};

export const handleFileUpload = async ({
  file,
  onSubmit,
  client,
  company,
  setLoading,
}) => {
  if (!company?.id) {
    showToast({
      variant: toastVariant.danger,
      title: 'Error',
      message:
        'Upload failed: company information not available. Please refresh and try again.',
    });
    return;
  }

  const fileUUID = uuidv4();
  const size = file?.size;

  if (size > MAX_FILE_UPLOAD_SIZE) {
    showToast({
      variant: toastVariant.danger,
      title: 'Error',
      message: 'File size too large',
    });
    throw new Error('File too large');
  }

  file = await convertHeicIfNeeded(file);
  const convertedName = file.name;
  const convertedExt = convertedName.split('.').pop().toLowerCase();

  const key = `assets/${company.id}/${fileUUID}.${convertedExt}`;
  setLoading(true);
  const { data } = await client.query({
    query: uploadUrlQuery,
    variables: { fileName: key, fileType: convertedExt },
    fetchPolicy: 'no-cache',
  });

  const signedUrl = data.simpleStorageUploadUrl;

  await axios.put(signedUrl, file, {
    headers: {
      'Content-Type':
        convertedExt === 'pdf' ? 'application/pdf' : file.type || convertedExt,
    },
  });

  const uploaded = {
    url: `${CF_DOMAIN({ company })}${fileUUID}.${convertedExt}`,
    name: convertedName,
    extension: convertedExt,
    size: file.size,
  };

  onSubmit(uploaded);
  return uploaded;
};
